// src/theme.ts
import { createTheme, Theme, alpha } from '@mui/material/styles';

// Professional fintech color palette
const financialColors = {
  // Primary blues - trust and professionalism
  navy: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  // Financial blue for accents
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  // Success green (conservative)
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  // Warning amber
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  // Error red
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
};

// 1. Light theme – professional fintech design
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: financialColors.blue[600], // #2563eb
      light: financialColors.blue[400], // #60a5fa
      dark: financialColors.blue[700], // #1d4ed8
      contrastText: '#ffffff',
    },
    secondary: {
      main: financialColors.navy[700], // #334155
      light: financialColors.navy[500], // #64748b
      dark: financialColors.navy[800], // #1e293b
      contrastText: '#ffffff',
    },
    error: {
      main: financialColors.error[600], // #dc2626
      light: financialColors.error[400], // #f87171
      dark: financialColors.error[700], // #b91c1c
      contrastText: '#ffffff',
    },
    warning: {
      main: financialColors.warning[500], // #f59e0b
      light: financialColors.warning[300], // #fcd34d
      dark: financialColors.warning[600], // #d97706
      contrastText: financialColors.navy[900], // Dark text for amber background
    },
    info: {
      main: financialColors.blue[500], // #3b82f6
      light: financialColors.blue[300], // #93c5fd
      dark: financialColors.blue[700], // #1d4ed8
      contrastText: '#ffffff',
    },
    success: {
      main: financialColors.success[600], // #16a34a
      light: financialColors.success[400], // #4ade80
      dark: financialColors.success[700], // #15803d
      contrastText: '#ffffff',
    },
    background: {
      default: financialColors.navy[50], // #f8fafc
      paper: '#ffffff',
    },
    text: {
      primary: financialColors.navy[900],   // #0f172a
      secondary: financialColors.navy[700], // #334155
      disabled: financialColors.navy[400],  // #94a3b8
    },
    divider: financialColors.navy[200], // #e2e8f0
    grey: financialColors.navy, // Using the full navy scale as grey
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'system-ui',
      'sans-serif',
    ].join(','),
    // NOTE: For better theme adaptability (e.g., text in dark sidebar of light theme),
    // consider removing hardcoded 'color' from these variants. They would then
    // inherit from palette.text.primary/secondary or from parent components.
    // The current setup is fine if these variants are primarily on light backgrounds.
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
      letterSpacing: '-0.025em',
      lineHeight: 1.2,
      color: financialColors.navy[900],
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.875rem',
      letterSpacing: '-0.025em',
      lineHeight: 1.25,
      color: financialColors.navy[900],
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '-0.025em',
      lineHeight: 1.375,
      color: financialColors.navy[900],
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '-0.025em',
      lineHeight: 1.4,
      color: financialColors.navy[900],
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      letterSpacing: '-0.025em',
      lineHeight: 1.5,
      color: financialColors.navy[900],
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '-0.025em',
      lineHeight: 1.5,
      color: financialColors.navy[800],
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      letterSpacing: '0.015em',
      lineHeight: 1.75,
      color: financialColors.navy[800],
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      letterSpacing: '0.015em',
      lineHeight: 1.57,
      color: financialColors.navy[800],
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.625,
      letterSpacing: '0.015em',
      color: financialColors.navy[800],
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.57,
      letterSpacing: '0.015em',
      color: financialColors.navy[700],
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.025em',
    },
    caption: { // FIX for light theme sidebar headers
      fontSize: '0.75rem',
      lineHeight: 1.66,
      letterSpacing: '0.03em',
      // color: financialColors.navy[600], // REMOVED: Allows inheriting color from Drawer
    },
    overline: { // FIX for light theme sidebar headers
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 2.66,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      // color: financialColors.navy[600], // REMOVED: Allows inheriting color from Drawer
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @page { margin: 1in; size: letter; }
        .print-container { break-inside: avoid; page-break-inside: avoid; }
        @media print {
          body { font-family: 'Inter', sans-serif !important; font-size: 11px !important; line-height: 1.4 !important; color: #000 !important; background: #fff !important; }
          h1, h2, h3, h4, h5, h6 { font-family: 'Inter', sans-serif !important; color: #000 !important; page-break-after: avoid; margin-top: 0 !important; }
          .MuiTypography-root { color: #000 !important; }
          .no-print { display: none !important; }
        }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${financialColors.navy[100]}; }
        ::-webkit-scrollbar-thumb { background: ${financialColors.navy[300]}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${financialColors.navy[400]}; }
      `,
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${financialColors.navy[200]}`,
          boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.08), 0 1px 2px 0 rgba(15, 23, 42, 0.06)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.12), 0 2px 4px -1px rgba(15, 23, 42, 0.08)',
            transform: 'translateY(-1px)',
          },
        }),
        elevation1: { boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.08), 0 1px 2px 0 rgba(15, 23, 42, 0.06)' },
        elevation2: { boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.12), 0 2px 4px -1px rgba(15, 23, 42, 0.08)' },
        elevation3: { boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.12), 0 4px 6px -2px rgba(15, 23, 42, 0.08)' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${financialColors.navy[200]}`,
          borderRadius: theme.shape.borderRadius,
          boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.08), 0 1px 2px 0 rgba(15, 23, 42, 0.06)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.12), 0 2px 4px -1px rgba(15, 23, 42, 0.08)',
            transform: 'translateY(-2px)',
            borderColor: financialColors.navy[300],
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '0.875rem',
          minHeight: 40,
          padding: '10px 20px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 'none',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.12), 0 2px 4px -1px rgba(0, 0, 0, 0.08)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          },
        }),
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: `0 4px 6px -1px ${alpha(theme.palette.primary.main, 0.25)}, 0 2px 4px -1px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
        }),
        containedSecondary: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
          },
        }),
        outlined: ({ theme }) => ({
          borderColor: financialColors.navy[300],
          color: financialColors.navy[800],
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.04), // Lighter hover for outlined
          },
        }),
        text: ({ theme }) => ({ // Default text button color for light theme
          color: financialColors.navy[800],
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.04), // Lighter hover
          },
        }),
      },
      variants: [
        {
          props: { variant: 'gradient' },
          style: ({ theme }) => ({
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              boxShadow: `0 8px 25px 0 ${alpha(theme.palette.primary.main, 0.25)}`,
            },
          }),
        },
      ],
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${financialColors.navy[200]}`,
          overflow: 'hidden',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: financialColors.navy[50], // Very light background for table head
        },
      },
    },
    MuiTableCell: { // Styles for light theme table cells
      styleOverrides: {
        head: ({ theme }) => ({
          fontWeight: 600,
          fontSize: '0.875rem',
          color: financialColors.navy[700], // Dark text for light head background
          backgroundColor: financialColors.navy[100], // Light head background
          borderBottom: `1px solid ${financialColors.navy[300]}`,
          padding: theme.spacing(2),
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }),
        body: ({ theme }) => ({
          fontSize: '0.875rem',
          color: financialColors.navy[700], // Darker text for body cells
          borderBottom: `1px solid ${financialColors.navy[200]}`,
          padding: theme.spacing(2),
        }),
      },
    },
    MuiAppBar: { // Light theme AppBar
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff', // White AppBar for light theme
          color: financialColors.navy[900], // Dark text
          boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.08), 0 1px 2px 0 rgba(15, 23, 42, 0.06)',
          borderBottom: `1px solid ${financialColors.navy[200]}`,
        },
      },
    },
    MuiDrawer: { // FIX for light theme sidebar text
      styleOverrides: {
        paper: {
          backgroundColor: financialColors.navy[900], // Dark sidebar background
          color: '#ffffff', // Default text inside is white
          // borderRight: `1px solid ${financialColors.navy[700]}`, // Kept from dark theme, adjust if needed for light
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.background.paper, // White background for inputs
            transition: 'all 0.2s ease',
            '& input::placeholder': { // Placeholder styling for light theme
              color: financialColors.navy[500], // Darker gray for placeholder
              opacity: 0.8,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: financialColors.navy[400],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
            },
          },
          '& .MuiInputLabel-root': { // Label styling for light theme
            color: financialColors.navy[600],
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiChip: { /* ... Your existing lightTheme MuiChip styles ... */ },
    MuiAlert: { /* ... Your existing lightTheme MuiAlert styles ... */ },
  },
});

// 2. Dark theme - professional dark mode with high contrast
export const darkTheme = createTheme({
  // Spread lightTheme to inherit typography structure, shape, spacing etc.
  // We will override palette and specific components/typography colors.
  ...lightTheme,
  palette: {
    mode: 'dark',
    primary: { // FIX for "too light" primary links/actions in dark mode
      main: financialColors.blue[300], // #93c5fd (Brighter blue)
      light: financialColors.blue[200], // #bfdbfe
      dark: financialColors.blue[400],  // #60a5fa
      contrastText: financialColors.navy[900], // Dark text for this light blue
    },
    secondary: {
      main: financialColors.navy[300], // #cbd5e1
      light: financialColors.navy[200], // #e2e8f0
      dark: financialColors.navy[400], // #94a3b8
      contrastText: financialColors.navy[900], // Dark text for this light secondary
    },
    error: {
      main: financialColors.error[400], // #f87171
      light: financialColors.error[300], // #fca5a5
      dark: financialColors.error[500], // #ef4444
      contrastText: financialColors.navy[900], // Dark text for light red
    },
    warning: {
      main: financialColors.warning[400], // #fbbf24
      light: financialColors.warning[300], // #fcd34d
      dark: financialColors.warning[500], // #f59e0b
      contrastText: financialColors.navy[900], // Dark text for light amber
    },
    info: { // Using primary settings for info in dark mode, as it's a bright blue
      main: financialColors.blue[300], // #93c5fd
      light: financialColors.blue[200], // #bfdbfe
      dark: financialColors.blue[400],  // #60a5fa
      contrastText: financialColors.navy[900], // Dark text for this light blue
    },
    success: {
      main: financialColors.success[400], // #4ade80
      light: financialColors.success[300], // #86efac
      dark: financialColors.success[500], // #22c55e
      contrastText: financialColors.navy[900], // Dark text for light green
    },
    background: {
      default: financialColors.navy[900], // #0f172a
      paper: financialColors.navy[800], // #1e293b
    },
    text: {
      primary: '#ffffff',
      secondary: financialColors.navy[200], // #e2e8f0 (was navy[200] - ensuring this is bright)
      disabled: financialColors.navy[500],  // #64748b (Slightly darker disabled for subtle contrast)
    },
    divider: financialColors.navy[700], // #334155 (Darker divider for dark bg)
    grey: { // Inverted grey scale for dark mode if needed, or use as is
      50: financialColors.navy[900],
      100: financialColors.navy[800],
      200: financialColors.navy[700],
      300: financialColors.navy[600],
      400: financialColors.navy[500],
      500: financialColors.navy[400],
      600: financialColors.navy[300],
      700: financialColors.navy[200],
      800: financialColors.navy[100],
      900: financialColors.navy[50],
    },
  },
  typography: { // Override all typography colors from lightTheme for dark mode
    ...lightTheme.typography, // Spread structure
    h1: { ...lightTheme.typography?.h1, color: '#ffffff' },
    h2: { ...lightTheme.typography?.h2, color: '#ffffff' },
    h3: { ...lightTheme.typography?.h3, color: '#ffffff' },
    h4: { ...lightTheme.typography?.h4, color: financialColors.navy[100] }, // #f1f5f9
    h5: { ...lightTheme.typography?.h5, color: financialColors.navy[100] }, // #f1f5f9
    h6: { ...lightTheme.typography?.h6, color: financialColors.navy[200] }, // #e2e8f0
    subtitle1: { ...lightTheme.typography?.subtitle1, color: financialColors.navy[200] },
    subtitle2: { ...lightTheme.typography?.subtitle2, color: financialColors.navy[200] },
    body1: { ...lightTheme.typography?.body1, color: '#ffffff' },
    body2: { ...lightTheme.typography?.body2, color: financialColors.navy[200] },
    button: { ...lightTheme.typography?.button, /* color: '#ffffff' if needed, but buttons handle their own */ },
    caption: { ...lightTheme.typography?.caption, color: financialColors.navy[300] }, // #cbd5e1 (brighter than navy[200] for smaller text)
    overline: { ...lightTheme.typography?.overline, color: financialColors.navy[300] }, // #cbd5e1
  },
  components: {
    // Spread light theme components and override specific ones for dark mode
    ...lightTheme.components,
    MuiCssBaseline: {
      styleOverrides: (themeParam: Theme) => `
        ${typeof lightTheme.components?.MuiCssBaseline?.styleOverrides === 'function'
          ? lightTheme.components.MuiCssBaseline.styleOverrides(themeParam)
          : lightTheme.components?.MuiCssBaseline?.styleOverrides || ''}
        // Dark mode scrollbars
        ::-webkit-scrollbar-track { background: ${financialColors.navy[800]}; }
        ::-webkit-scrollbar-thumb { background: ${financialColors.navy[600]}; }
        ::-webkit-scrollbar-thumb:hover { background: ${financialColors.navy[500]}; }
      `,
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper, // Uses darkTheme.palette.background.paper
          border: `1px solid ${financialColors.navy[700]}`,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.16)', // Softer dark shadows
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.16)',
            borderColor: financialColors.navy[600],
          },
        }),
      },
    },
    MuiCard: { // Assuming MuiCard should be similar to MuiPaper in dark mode
      styleOverrides: {
        root: ({ theme }) => ({
          ...(lightTheme.components?.MuiCard?.styleOverrides?.root as any || {}), // Spread light theme card root
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${financialColors.navy[700]}`,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.16)',
          '&:hover': {
            ...(lightTheme.components?.MuiCard?.styleOverrides?.root as any || {})?.['&:hover'],
            borderColor: financialColors.navy[600],
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.16)',
            transform: 'translateY(-2px)', // Retain hover transform
          },
        }),
      },
    },
    MuiButton: {
      // Inherit root, containedPrimary, containedSecondary, variants from lightTheme
      // and override specific ones for dark mode.
      ...lightTheme.components?.MuiButton,
      styleOverrides: {
        // Spread all style overrides from lightTheme MuiButton
        ...(typeof lightTheme.components?.MuiButton?.styleOverrides === 'object' ? lightTheme.components.MuiButton.styleOverrides : {}),
        // Override for specific button types in dark mode
                 root: (args) => {
           const lightRootStyle = typeof lightTheme.components?.MuiButton?.styleOverrides?.root === 'function'
             ? lightTheme.components.MuiButton.styleOverrides.root(args)
             : lightTheme.components?.MuiButton?.styleOverrides?.root || {};
           const lightHover = typeof lightRootStyle === 'object' && lightRootStyle && '&:hover' in lightRootStyle
             ? (lightRootStyle as any)['&:hover'] : {};
           return {
             ...(typeof lightRootStyle === 'object' ? lightRootStyle : {}),
             '&:hover': {
               ...lightHover,
               boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3), 0 2px 4px -1px rgba(0,0,0,0.22)',
             }
           };
         },
        containedPrimary: ({ theme }) => ({ // Uses new darkTheme.palette.primary
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText, // Will be navy[900] (dark text)
          '&:hover': {
            backgroundColor: theme.palette.primary.dark, // E.g., blue[400]
            boxShadow: `0 4px 6px -1px ${alpha(theme.palette.primary.main, 0.3)}, 0 2px 4px -1px ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        }),
        containedSecondary: ({ theme }) => ({ // Uses new darkTheme.palette.secondary
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText, // Will be navy[900] (dark text)
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark, // E.g. navy[400]
          }
        }),
        outlined: ({ theme, ownerState }) => ({
          borderColor: ownerState.color === 'primary' ? theme.palette.primary.main :
            ownerState.color === 'secondary' ? theme.palette.secondary.main :
              financialColors.navy[500], // Default outlined border
          color: ownerState.color === 'primary' ? theme.palette.primary.main :
            ownerState.color === 'secondary' ? theme.palette.secondary.main :
              theme.palette.text.secondary, // Default text color
          '&:hover': {
            borderColor: ownerState.color === 'primary' ? theme.palette.primary.dark :
              ownerState.color === 'secondary' ? theme.palette.secondary.dark :
                theme.palette.text.primary,
            backgroundColor: alpha(
              ownerState.color === 'primary' ? theme.palette.primary.main :
                ownerState.color === 'secondary' ? theme.palette.secondary.main :
                  theme.palette.text.primary, 0.08),
          },
        }),
        text: ({ theme, ownerState }) => ({ // Text buttons in dark mode
          color: ownerState.color === 'primary' ? theme.palette.primary.main :
            ownerState.color === 'secondary' ? theme.palette.secondary.main :
              theme.palette.text.secondary, // Default to bright secondary text
          '&:hover': {
            backgroundColor: alpha(
              ownerState.color === 'primary' ? theme.palette.primary.main :
                ownerState.color === 'secondary' ? theme.palette.secondary.main :
                  theme.palette.text.primary, 0.08),
          },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: financialColors.navy[800], // Dark AppBar background
          color: financialColors.navy[100],      // Light text on AppBar
          borderBottom: `1px solid ${financialColors.navy[700]}`,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.2), 0 1px 2px 0 rgba(0,0,0,0.16)', // Dark mode shadow
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { // Dark theme Drawer should already be dark from lightTheme, but ensure text is bright
          backgroundColor: financialColors.navy[900],
          color: '#ffffff', // Ensure default text is white
          borderRight: `1px solid ${financialColors.navy[700]}`,
        },
      },
    },
    MuiLink: { // FIX for dark theme footer links / general links
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary, // #e2e8f0 (bright by default)
          textDecorationColor: alpha(theme.palette.text.secondary, 0.5),
          '&:hover': {
            color: theme.palette.text.primary, // #ffffff
            textDecorationColor: theme.palette.text.primary,
          },
          // For links that use primary color:
          '&[href][color="primary"]': { // This won't work directly, color prop on Link is different
            color: theme.palette.primary.main, // Will be the new bright blue
            textDecorationColor: alpha(theme.palette.primary.main, 0.5),
            '&:hover': {
              color: theme.palette.primary.light,
              textDecorationColor: theme.palette.primary.light,
            }
          }
        }),
      },
    },
    MuiTableCell: { // Styles for dark theme table cells
      styleOverrides: {
        head: ({ theme }: { theme: Theme }) => ({
          fontWeight: 600,
          fontSize: '0.875rem',
          color: '#ffffff', // Pure white for table headers
          backgroundColor: financialColors.navy[700], // Darker head background
          borderBottom: `1px solid ${financialColors.navy[600]}`,
          padding: theme.spacing(2),
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }),
        body: ({ theme }: { theme: Theme }) => ({
          fontSize: '0.875rem',
          color: financialColors.navy[100], // Very bright text for table cells
          borderBottom: `1px solid ${financialColors.navy[700]}`,
          padding: theme.spacing(2),
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: theme.shape.borderRadius,
            backgroundColor: financialColors.navy[700], // Slightly lighter input bg for dark mode
            color: '#ffffff',
            transition: 'all 0.2s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: financialColors.navy[600],
            },
            '& input::placeholder': { // Placeholder for dark theme
              color: financialColors.navy[400], // #94a3b8
              opacity: 0.7,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: financialColors.navy[400], // Lighter border on hover
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main, // Uses new bright blue primary
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
            },
          },
          '& .MuiInputLabel-root': {
            color: financialColors.navy[300], // Brighter labels
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.primary.main, // Uses new bright blue primary
          },
        }),
      },
    },
    MuiChip: { /* ... Ensure darkTheme MuiChip styles are appropriate ... */ },
    MuiAlert: { /* ... Ensure darkTheme MuiAlert styles are appropriate ... */ },
  },
});

// Export themes with type augmentation for custom variants
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    gradient: true;
  }
}
// If you use MuiLink color prop extensively, you might need to augment it too.
// declare module '@mui/material/Link' {
//   interface LinkPropsColorOverrides {
//     // Define custom color names if you add them to palette
//   }
// }

export default { lightTheme, darkTheme };